import React, { useState } from "react";
import DataTable from "react-data-table-component";
import { HiX, HiDotsVertical } from "react-icons/hi";
import { FilterBar, FilterField } from "../common/FilterControls";
import { Menu, Transition } from "@headlessui/react";
import AddScrapModal from "../Modal/addscrapmodal/AddScrapModal";
import ViewScrapSaleModal from "../Modal/addscrapmodal/ViewScrapSaleModal";

const AllSplitTable = ({
  splitItemPermission,
  columns,
  data,
  onItemAdded,
  updateItemRecordPermissions,
  filterColumns,
}) => {
  const [selectedItemData, setSelectedItemData] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [filterText, setFilterText] = useState("");

  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  // SGEW 19 Oct (field audit): "Category" searched the record's internal id (typing a name found nothing); the list
  // searches the type and the name, a page can pass its own columns (the sales list: what was sold, the date)
  const allowedFilterColumns = filterColumns || [
    { name: "Type", selector: "type" },
    { name: "Name", selector: (row) => `${row.category_name || ""} ${row.note || ""}` },
  ];
  const [selectedColumn, setSelectedColumn] = useState(allowedFilterColumns[0].name);

  // Declare actionColumn first
  const actionColumn = {
    id: "actions", name: "Actions", width: "80px",
    cell: (row) => (
      <Menu as="div" className="relative">
        <Menu.Button className="sgew-row-actions">
          <HiDotsVertical className="w-6 h-6 text-gray-500 cursor-pointer" />
        </Menu.Button>
        <Transition
          enter="transition duration-100 ease-out"
          enterFrom="transform scale-95 opacity-0"
          enterTo="transform scale-100 opacity-100"
          leave="transition duration-75 ease-out"
          leaveFrom="transform scale-100 opacity-100"
          leaveTo="transform scale-95 opacity-0"
        >
          <Menu.Items anchor="bottom end" className="z-50 w-48 bg-white shadow-lg rounded-md ring-1 ring-black ring-opacity-5 focus:outline-none [--anchor-gap:4px]">
            <Menu.Item>
              {({ active }) => (
                <button
                  className={`${active ? "bg-[#0074ff] text-white" : "text-gray-900"} group flex w-full items-center rounded-md px-4 py-2 text-sm`}
                  onClick={() => {
                    setSelectedItemId(row?.id);
                    if (columns.some(col => col.name === "Amount")) {
                      setOpenViewModal(true); 
                    } else { 
                      setSelectedItemData(row);
                      setOpenModal(true); 
                    }
                  }}
                >
                  View
                </button>
              )}
            </Menu.Item>
          </Menu.Items>
        </Transition>
      </Menu>
    ),
    ignoreRowClick: true,
    allowOverflow: true,
    button: true,
    right: true,
    style: {
      position: "sticky",
      right: "0px",
      zIndex: 1,
      backgroundColor: "inherit", // covers the cells that scroll under the pinned column (SGEW 17 Oct)
      boxShadow: "-10px 0 10px -10px rgba(0, 0, 0, 0.15)",
    },
  };

  // Now include actionColumn inside enhancedColumns
  const enhancedColumns = [...columns, actionColumn];

  // Function to get column value by either string key or selector function
  const getColumnValue = (row, selector) => {
    if (typeof selector === "function") {
      return selector(row);
    }
    return row[selector] || "";
  };

  // Filter data by the selected column and filter text
  const filteredData = data.filter((item) => {
    const column = allowedFilterColumns.find(
      (col) => col.name === selectedColumn
    );

    if (!column) {
      return true; // No filter if no column is selected
    }

    const valueToFilter = getColumnValue(item, column.selector)
      .toString()
      .toLowerCase();
    return valueToFilter.includes(filterText.toLowerCase());
  });

  const handleClear = () => {
    if (filterText) {
      setResetPaginationToggle(!resetPaginationToggle);
      setFilterText("");
    }
  };

  // Sub-header: pick the column, then type to filter it (SGEW 17 Oct: the shared filter-bar look - labelled, 38px high)
  const subHeaderComponent = (
    <FilterBar className="w-full py-2 text-left">
      <FilterField label="Search in" kind="select">
        <select
          className="sgew-control"
          value={selectedColumn}
          onChange={(e) => {
            setSelectedColumn(e.target.value);
            setFilterText(""); // Clear filter text when changing column
          }}
        >
          {allowedFilterColumns.map((col) => (
            <option key={col.name} value={col.name}>
              {col.name}
            </option>
          ))}
        </select>
      </FilterField>
      <FilterField label="Search" kind="grow">
        <span className="relative block">
          <input
            className="sgew-control pr-8"
            type="text"
            placeholder={`Filter by ${selectedColumn}`}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
          {filterText && (
            <HiX
              className="absolute right-2.5 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500"
              onClick={handleClear}
            />
          )}
        </span>
      </FilterField>
    </FilterBar>
  );

  return (
    <div className="font-poppins">
      <div className="overflow-hidden !bg-white rounded-[0.42vw]">
        <DataTable
          columns={enhancedColumns}
          data={filteredData}
          pagination
          subHeader
          subHeaderComponent={subHeaderComponent}
          paginationResetDefaultPage={resetPaginationToggle}
          persistTableHead
          customStyles={{
            rows: {
              style: {
                minHeight: "60px",
                fontSize: "clamp(12px,0.93vw, 1rem)",
              },
            },
            headCells: {
              style: {
                fontWeight: 600,
                textAlign: "center",
                whiteSpace: "normal",
                fontSize: "clamp(14px, 1.1vw, 1.1rem)",
                color: "#10131e",
              },
            },
          }}
        />
      </div>
      {openModal && (
        <AddScrapModal
          open={openModal}
          handleClose={() => setOpenModal(false)}
          Item={selectedItemId}
          onItemAdded={onItemAdded} 
          selectedItemData={selectedItemData}
        />
      )}
      {/* View Scrap Sale Modal */}
      {openViewModal && (
        <ViewScrapSaleModal
          open={openViewModal}
          onClose={() => setOpenViewModal(false)}
          saleId={selectedItemId}
        />
      )}
    </div>
  );
};

export default AllSplitTable;
